import { narrativeText } from "@hamlak/shared";
import { ActionButton, Callout } from "@hamlak/ui";
import { NarrativeText } from "@/components/NarrativeText";
import { t } from "@/i18n/ko";
import { useLongPress } from "@/lib/useLongPress";
import type { Speaker } from "@/features/plot/blocks/BlockRenderer";
import type { TurnError } from "./useTurnStream";

/** 길게 누른 메시지. messageId는 서버 메시지(turns.id)일 때만 — 캐릭터 메시지면 신고 대상(1.21). */
export type LongPressTarget = { text: string; role: "user" | "character"; messageId?: string };
type LongPress = { onLongPress?: ((target: LongPressTarget) => void) | undefined; messageId?: string };

/** 복사 원문 = 마크다운 마커 제거한 표시 텍스트. */
export const displayText = (raw: string) => narrativeText(raw);

/** 캐릭터 응답(who 행 = speaker). streaming이면 커서, 길게 누르기(스트리밍 중 비활성) → 복사·신고. onRegenerate 면 ↻(마지막 답변, CHT-08), caption 은 본문 아래 보조 한 줄. */
export function CharacterTurn({
  text,
  streaming = false,
  speaker,
  onLongPress,
  messageId,
  onRegenerate,
  caption,
}: { text: string; streaming?: boolean; speaker?: Speaker | undefined; onRegenerate?: (() => void) | undefined; caption?: string | undefined } & LongPress) {
  const press = useLongPress(() => onLongPress?.({ text: displayText(text), role: "character", ...(messageId ? { messageId } : {}) }), streaming || !onLongPress);
  return (
    <div className="chat__turn chat__turn--character" data-testid="message-character" data-streaming={streaming || undefined} {...press}>
      <NarrativeText
        text={text}
        streaming={streaming}
        {...(speaker ? { name: speaker.name, ...(speaker.avatarUrl ? { avatar: <img src={speaker.avatarUrl} alt="" /> } : {}) } : {})}
        {...(onRegenerate ? { onRegenerate } : {})}
      >
        {caption && <p className="chat__caption">{caption}</p>}
      </NarrativeText>
    </div>
  );
}

/** 내 메시지. 캐릭터 응답과 같은 서식 규약(docs/narrative-format.md, AC0-254)을 오른쪽으로: `*지문*` 은 말풍선 밖 회색, 대사는 내 말풍선, `> 장면`·`'속마음'`·`**굵게**`. pending(전송 중)은 흐리게. */
export function UserTurn({ text, pending = false, onLongPress, messageId }: { text: string; pending?: boolean } & LongPress) {
  const press = useLongPress(() => onLongPress?.({ text: displayText(text), role: "user", ...(messageId ? { messageId } : {}) }), pending || !onLongPress);
  return (
    <div className="chat__turn chat__turn--user" data-testid="message-user" data-pending={pending || undefined} {...press}>
      <NarrativeText text={text} mine />
    </div>
  );
}

/** 1.12 실패·환불 Callout. retryable이면 "다시 시도"(같은 clientTurnId). CONTENT_FILTERED는 재시도 대신 입력창 포커스. 리롤 실패(F03-22)는 제목·안내 한 줄을 바꿔 쓴다. */
export function TurnFailure({ error, onRetry, onFocusInput, title = t("turn.error.title"), note }: { error: TurnError; onRetry: () => void; onFocusInput: () => void; title?: string; note?: string }) {
  const filtered = error.code === "CONTENT_FILTERED";
  const description = [error.message, note ?? null, error.refunded ? t("turn.error.refunded", { refunded: error.refunded }) : null].filter(Boolean).join(" ");
  return (
    <div className="chat__failure" data-testid="turn-failure">
      <Callout tone="critical" title={title} description={description} />
      {error.retryable && !filtered && (
        <ActionButton variant="neutralWeak" size="small" onClick={onRetry}>
          {t("turn.retry")}
        </ActionButton>
      )}
      {filtered && (
        <ActionButton variant="neutralWeak" size="small" onClick={onFocusInput}>
          {t("turn.rewrite")}
        </ActionButton>
      )}
    </div>
  );
}
